if (!customElements.get('ns-home-banner-film')) {
  customElements.define('ns-home-banner-film', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.videos = Array.from(this.querySelectorAll('video'));
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isMuted = this.isMuted !== false;
      this.isInView = false;
      this.videoCycle = this.closest('slideshow-component')?.dataset.videoCycle === 'true';
      if (this.videoCycle) this.dataset.filmAutoplay = 'true';
      this.mobileQuery.addEventListener('change', () => {
        this.sync();
        this.dispatchEvent(new CustomEvent('ns:banner-film-resize', { bubbles: true }));
      }, { signal });
      this.motionQuery.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.querySelector('[data-film-pause]')?.addEventListener('click', () => {
        if (!this.video) return;
        if (this.video.paused) {
          this.userPaused = false;
          this.userStarted = true;
          this.completed = false;
        } else {
          this.userPaused = true;
        }
        this.sync();
      }, { signal });
      this.querySelector('[data-film-sound]')?.addEventListener('click', () => {
        this.isMuted = !this.isMuted;
        this.videos.forEach(video => { video.muted = this.isMuted; });
        this.updateControls();
      }, { signal });
      this.videos.forEach(video => {
        video.removeAttribute('autoplay');
        if (this.videoCycle) video.loop = false;
        video.muted = this.isMuted;
        ['play', 'pause', 'volumechange'].forEach(name => video.addEventListener(name, () => this.updateControls(), { signal }));
        video.addEventListener('ended', () => {
          if (video !== this.video) return;
          this.completed = true;
          this.updateControls();
          this.dispatchEvent(new CustomEvent('ns:banner-film-ended', { bubbles: true }));
        }, { signal });
      });
      this.observer = new IntersectionObserver(entries => {
        this.isInView = entries.some(entry => entry.isIntersecting);
        this.sync();
      }, { threshold: 0 });
      this.observer.observe(this);
      this.sync();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.videos?.forEach(video => video.pause());
    }

    setSlideActive(active) {
      if (active && !this.isSlideActive && this.videoCycle) {
        this.completed = false;
        this.userPaused = false;
        this.videos.forEach(video => { video.currentTime = 0; });
      }
      this.isSlideActive = active;
      if (this.events) this.sync();
    }

    canPlay() {
      return this.isConnected && this.isSlideActive && this.isInView && !document.hidden &&
        !this.userPaused && !this.completed &&
        (this.userStarted || (this.dataset.filmAutoplay === 'true' && !this.motionQuery.matches));
    }

    sync() {
      const desktop = this.querySelector('.ns-home-film__desktop video');
      const mobile = this.querySelector('.ns-home-film__mobile video');
      this.video = this.mobileQuery.matches && mobile ? mobile : desktop || mobile;
      this.videos.forEach(video => {
        video.muted = this.isMuted;
        if (video !== this.video) video.pause();
      });
      if (this.video) {
        if (this.canPlay()) {
          const video = this.video;
          if (video.paused) video.play().then(() => {
            if (video !== this.video || !this.canPlay()) video.pause();
            this.updateControls();
          }).catch(() => this.updateControls());
        } else {
          this.video.pause();
        }
      }
      this.updateControls();
    }

    updateControls() {
      const pause = this.querySelector('[data-film-pause]');
      const sound = this.querySelector('[data-film-sound]');
      if (pause) {
        const paused = !this.video || this.video.paused;
        pause.setAttribute('aria-label', paused ? 'Play brand film' : 'Pause brand film');
        pause.innerHTML = paused
          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7V5Z"/></svg>'
          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14"/></svg>';
      }
      if (sound) {
        sound.setAttribute('aria-label', this.isMuted ? 'Turn sound on' : 'Mute brand film');
        sound.setAttribute('aria-pressed', String(!this.isMuted));
        sound.innerHTML = this.isMuted
          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Zm5 4 5 6m0-6-5 6"/></svg>'
          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Zm5 3a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/></svg>';
      }
    }
  });
}

if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);

        this.selectedIndex = this.selectedIndex;

        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }

        this.initSlider();
        this.addEventListener('ns:banner-film-resize', () => {
          if (this.dataset.videoCycle !== 'true') {
            requestAnimationFrame(() => this.sliderInstance?.slider?.updateAutoHeight(0));
          }
        });
        this.addEventListener('ns:banner-film-ended', (event) => {
          const slide = event.target.closest('.swiper-slide');
          if (slide?.classList.contains('swiper-slide-active') && Number(this.dataset.autoplay) > 0 && this.items > 1) {
            this.sliderInstance.slider.slideNext();
          }
        });
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        if (this.items === 1 && this.querySelector('ns-home-banner-film')) this.classList.add('ns-home-film-only');
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          autoHeight: Boolean(this.querySelector('ns-home-banner-film')) && this.dataset.videoCycle !== 'true',
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0 && this.dataset.videoCycle !== 'true') {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
        this.sliderInstance.slider.on('loopFix', swiper => this.syncHomeFilms(swiper.slides[swiper.activeIndex]));
        this.sliderInstance.slider.on('slideChangeTransitionEnd', swiper => this.syncHomeFilms(swiper.slides[swiper.activeIndex]));

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
                if (
                  typeof motionTextElement.resetAnimation === 'function' &&
                  !motionTextElement.closest('.swiper-slide-active')
                ) {
                  motionTextElement.resetAnimation();
                }
              });
          }
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                this.sliderInstance.slider.slideToLoop(slideToIndex);
              });
            });
          } else {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              });
            });
          }
        }
      }

      onReady(selectedElement) {
        this.syncHomeFilms(selectedElement);
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            motionEl && motionEl.refreshAnimation();
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.syncHomeFilms(slides[activeIndex]);
        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      syncHomeFilms(activeSlide) {
        const films = this.querySelectorAll('ns-home-banner-film');
        if (!films.length) return;
        films.forEach(film => film.setSlideActive(film.closest('.swiper-slide') === activeSlide));
        if (this.dataset.videoCycle === 'true') {
          if (this.cycleSlide !== activeSlide) {
            clearTimeout(this.homeImageTimer);
            this.cycleSlide = activeSlide;
            if (activeSlide && !activeSlide.querySelector('ns-home-banner-film')) {
              this.homeImageTimer = setTimeout(() => {
                if (this.isConnected && this.cycleSlide === activeSlide && activeSlide.classList.contains('swiper-slide-active')) {
                  this.sliderInstance.slider.slideNext();
                }
              }, Number(this.dataset.autoplay));
            }
          }
          return;
        }
        // Let an uploaded film finish instead of cutting it off at the image-slide timer.
        const autoplay = this.sliderInstance?.slider?.autoplay;
        if (activeSlide?.querySelector('ns-home-banner-film')) autoplay?.stop();
        else if (Number(this.dataset.autoplay) > 0 && this.items > 1) autoplay?.start();
        this.sliderInstance?.slider?.updateAutoHeight(0);
      }

      disconnectedCallback() {
        clearTimeout(this.homeImageTimer);
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
